/**
 * Import Enrollments Dialog (iter269 — extracted from Enrollments.js).
 *
 * CSV upload + dry-run results viewer. The file ref lives in the parent
 * so we can reset the input after a successful import. Action handlers
 * (handleImport, handleDownloadTemplate) are passed in.
 *
 * Props:
 *   - open                    (bool)
 *   - onOpenChange            ((bool) => void)  — note: the parent uses
 *                                                this as `resetImportDialog`
 *   - fileInputRef            (React.MutableRefObject<HTMLInputElement | null>)
 *   - setImportFile           ((File) => void)
 *   - importFile              (File | null)
 *   - createSubscriptions     (bool)
 *   - setCreateSubscriptions  ((bool) => void)
 *   - importLoading           (bool)
 *   - importResults           (object | null)
 *   - onImport                (() => Promise<void>)
 *   - onDownloadTemplate      (() => void)
 */
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Checkbox } from '../../../components/ui/checkbox';
import { Upload, Download, Loader2 } from 'lucide-react';

export default function ImportEnrollmentsDialog({
  open,
  onOpenChange,
  fileInputRef,
  setImportFile,
  importFile,
  createSubscriptions,
  setCreateSubscriptions,
  importLoading,
  importResults,
  onImport,
  onDownloadTemplate,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Import Enrollments
          </DialogTitle>
          <DialogDescription>
            Upload a CSV file to bulk import enrollments. Use the template to ensure correct format.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {!importResults ? (
            <>
              <div className="space-y-2">
                <Label>CSV File</Label>
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={(e) => setImportFile(e.target.files[0])}
                  data-testid="import-file-input"
                />
                <p className="text-xs text-slate-500">
                  Required columns: player_name or player_email, program_name or program_id
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="create-subscriptions"
                  checked={createSubscriptions}
                  onCheckedChange={setCreateSubscriptions}
                />
                <Label htmlFor="create-subscriptions" className="text-sm font-normal">
                  Create subscriptions for imported enrollments (based on billing dates)
                </Label>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">Import Fields</h4>
                <div className="text-sm text-blue-800 space-y-1">
                  <p><strong>Player:</strong> player_name, player_email, or player_numeric_id (P-1001)</p>
                  <p><strong>Program:</strong> program_name or program_id</p>
                  <p><strong>Classes:</strong> class_names (semicolon-separated, e.g., "Mon 4PM;Wed 4PM")</p>
                  <p><strong>Billing:</strong> payment_frequency, monthly_fee, start_date, next_billing_date</p>
                  <p><strong>Status:</strong> pending, approved, current, paused, cancelled</p>
                </div>
                <Button
                  variant="link"
                  className="p-0 h-auto mt-2 text-blue-600"
                  onClick={onDownloadTemplate}
                >
                  <Download className="h-4 w-4 mr-1" />
                  Download Import Template
                </Button>
              </div>
            </>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{importResults.created}</p>
                  <p className="text-sm text-green-700">Created</p>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">{importResults.updated}</p>
                  <p className="text-sm text-blue-700">Updated</p>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <p className="text-2xl font-bold text-purple-600">{importResults.subscriptions_created}</p>
                  <p className="text-sm text-purple-700">Subscriptions</p>
                </div>
              </div>

              {importResults.errors?.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-medium text-red-900 mb-2">Errors ({importResults.errors.length})</h4>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {importResults.errors.map((err, idx) => (
                      <p key={idx} className="text-sm text-red-700">
                        Row {err.row}: {err.error}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {importResults.warnings?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h4 className="font-medium text-amber-900 mb-2">Warnings ({importResults.warnings.length})</h4>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {importResults.warnings.map((warn, idx) => (
                      <p key={idx} className="text-sm text-amber-700">
                        Row {warn.row}: {warn.warning}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {importResults ? 'Close' : 'Cancel'}
          </Button>
          {!importResults && (
            <Button onClick={onImport} disabled={!importFile || importLoading} data-testid="import-submit-btn">
              {importLoading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Importing...</>
              ) : (
                <><Upload className="h-4 w-4 mr-2" /> Import</>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
